import React, { useState } from "react"

import { useNavigate } from "react-router-dom"

import { ROUTES } from "../../../config/routes"
import { STEPS } from "../../../enums/ateliers"
import { PAGES } from "../../../enums/pages"
import ConfirmationDialog from "./components/ConfirmationDialog"
import Pioche from "./components/Pioche"
import Plateau from "./components/Plateau"
import Presentation from "./components/Presentation"
import Reglages from "./components/Reglages"
import Resultats from "./components/Resultats"
import { DetectiveCluxConfig } from "./config/interfaces"
import { useDetectiveCluxGame } from "./useDetectiveCluxGame"

import "./style.scss"

interface DetectiveCluxProps {
  config: DetectiveCluxConfig
}

/**
 * Moteur générique de l'atelier « Détective Clux » : retrouver le coupable, l'objet et le lieu
 * en écartant les tuiles grâce aux indices de la pioche.
 * Le thème (textes, images, éléments) est fourni par la configuration de chaque implémentation.
 */
const DetectiveClux = ({ config }: DetectiveCluxProps) => {
  const navigate = useNavigate()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const {
    state,
    currentClue,
    disabledTileIds,
    suspects,
    solutionItems,
    canDraw,
    canShowPrevious,
    canShowNext,
    isSolved,
    shakingTileId,
    feedbackMessage,
    openEnvelope,
    chooseLevel,
    draw,
    showPrevious,
    showNext,
    selectTile,
    clearShake,
    showResults,
    restart,
  } = useDetectiveCluxGame(config.items)

  const { images } = config

  if (state.step === STEPS.PRESENTATION) {
    return (
      <Presentation
        title={config.title}
        background={images.fond}
        envelope={images.enveloppe}
        logo={images.logo}
        onOpen={openEnvelope}
      />
    )
  }

  // Sans niveau ni solution, la partie n'a pas commencé : on affiche le choix du niveau
  if (
    state.step === STEPS.PARAMETRES ||
    state.level === null ||
    solutionItems === null
  ) {
    return (
      <Reglages
        title={config.title}
        background={images.fondReglages}
        parchment={images.parchemin}
        texts={config.reglages}
        onChooseLevel={chooseLevel}
      />
    )
  }

  if (state.step === STEPS.RESULTATS) {
    return (
      <Resultats
        background={images.fond}
        images={images}
        messages={config.messages}
        isSolved={isSolved}
        solution={solutionItems}
        onRestart={restart}
        onQuit={() => navigate(ROUTES[PAGES.HOME].path)}
      />
    )
  }

  return (
    <>
      <Plateau
        title={config.title}
        background={images.fond}
        items={config.items}
        disabledTileIds={disabledTileIds}
        shakingTileId={shakingTileId}
        feedbackMessage={feedbackMessage}
        onSelectTile={selectTile}
        onShakeEnd={clearShake}
        pioche={
          <Pioche
            currentClue={currentClue}
            level={state.level}
            clueIndex={state.viewedClueIndex}
            drawnCount={state.drawnCount}
            totalClues={state.deck.length}
            canDraw={canDraw}
            canShowPrevious={canShowPrevious}
            canShowNext={canShowNext}
            canConfirm={suspects !== null}
            onDraw={draw}
            onShowPrevious={showPrevious}
            onShowNext={showNext}
            onConfirm={() => setIsConfirmationOpen(true)}
          />
        }
      />
      {suspects && (
        <ConfirmationDialog
          open={isConfirmationOpen}
          suspects={suspects}
          solution={solutionItems}
          level={state.level}
          images={images}
          messages={config.messages}
          onValidate={() => {
            setIsConfirmationOpen(false)
            showResults()
          }}
          onClose={() => setIsConfirmationOpen(false)}
        />
      )}
    </>
  )
}

export default DetectiveClux
